import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";

export default function Footer() {
  const { t } = useLang();
  const links = [
    ["about", t("footer_about")],
    ["services", t("nav_services")],
    ["bitdefender", t("nav_bitdefender")],
    ["projects", t("nav_projects")],
    ["team", t("footer_team")],
    ["contact", t("nav_contact")],
  ];
  return (
    <footer data-testid="site-footer" className="bg-[#070C17] border-t border-white/10">
      <div className="px-6 md:px-12 lg:px-24 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-9 h-9 bg-[#ED1C24] flex items-center justify-center font-display font-bold text-white text-lg -skew-x-6">
              K
            </span>
            <span className="font-display font-semibold text-lg text-white">
              KINOS<span className="text-[#ED1C24]">.</span>
            </span>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            {t("footer_blurb")}
          </p>
          <div className="flex gap-3 mt-6">
            {[
              { icon: Facebook, label: "Facebook" },
              { icon: Instagram, label: "Instagram" },
              { icon: Linkedin, label: "LinkedIn" },
            ].map(({ icon: Icon, label }) => (
              <a
                key={label}
                data-testid={`social-${label.toLowerCase()}`}
                href="#"
                onClick={(e) => e.preventDefault()}
                aria-label={label}
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors duration-300"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-5">
            {t("footer_explore")}
          </h4>
          <ul className="space-y-3 text-sm text-slate-400">
            {links.map(([id, label]) => (
              <li key={id}>
                <button
                  data-testid={`footer-link-${id}`}
                  onClick={() => scrollToId(id)}
                  className="hover:text-white transition-colors duration-300"
                >
                  {label}
                </button>
              </li>
            ))}
            <li>
              <Link data-testid="footer-link-downloads" to="/downloads" className="hover:text-white transition-colors duration-300">
                {t("nav_catalogue")}
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-support" to="/support" className="hover:text-white transition-colors duration-300">
                {t("support_label")}
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-partner" to="/partner" className="hover:text-white transition-colors duration-300">
                {t("partner_label")}
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-partner-dashboard" to="/partner#dashboard" className="hover:text-white transition-colors duration-300">
                {t("pdash_label")}
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-tracking" to="/tracking" className="hover:text-white transition-colors duration-300">
                {t("nav_track_mobile")}
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-cloud" to="/cloud" className="hover:text-white transition-colors duration-300">
                {t("cloud_label")}
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-status" to="/status" className="hover:text-white transition-colors duration-300">
                {t("nav_status")}
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-kit" to="/kit-builder" className="hover:text-white transition-colors duration-300">
                {t("nav_kit")}
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-cctv-kit" to="/cctv-kit-builder" className="hover:text-white transition-colors duration-300">
                {t("nav_cctv_kit")}
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-apps" to="/apps" className="hover:text-white transition-colors duration-300">
                {t("nav_apps")}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-5">
            {t("footer_services")}
          </h4>
          <ul className="space-y-3 text-sm text-slate-400">
            {t("footer_service_list").map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-5">
            {t("footer_contact")}
          </h4>
          <ul className="space-y-4 text-sm text-slate-400">
            <li className="flex gap-3">
              <MapPin size={16} className="text-[#FFC61A] shrink-0 mt-0.5" />
              Fatuhada, Dili, Timor-Leste
            </li>
            <li className="flex gap-3">
              <Phone size={16} className="text-[#FFC61A] shrink-0 mt-0.5" />
              +670 7713 8966 / 3311 480
            </li>
            <li className="flex gap-3">
              <Mail size={16} className="text-[#FFC61A] shrink-0 mt-0.5" />
              info@kinos.tl
            </li>
            <li className="flex gap-3">
              <Mail size={16} className="text-[#FFC61A] shrink-0 mt-0.5" />
              service@kinos.tl — {t("contact_support")}
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 px-6 md:px-12 lg:px-24 py-6 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500">
        <span>{t("footer_rights")}</span>
        <span>www.kinos.tl — Dili, Timor-Leste</span>
      </div>
    </footer>
  );
}
